import { getQuickAccessEnvironment, QuickAccessEnvironment } from 'src/components/widgets/leftAppSidebar/leftAppSidebarTypes';
import { useAppState } from 'src/shared/appState';
import type { ZakekeEnvironment } from './__generated__/dashboardShellQuery.graphql';

/**
 * The dashboard reads the whole platform: the catalogue, the alerts and the tools count the products and the
 * setup of every solution — Customizer, Configurator, Viewer — together, whichever one the seller is in.
 *
 * What stays with the solution in use is what creates something or belongs to its menu: the menu and the widget
 * layout built from it, the way a product is added, the first run and the AI slot. They read the same
 * `lastUsedTool` the menu follows, through {@link useDashboardEnvironment}.
 */
export interface DashboardEnvironment {
    /** The solution the seller is in. */
    environment: QuickAccessEnvironment;
}

/**
 * The variables the data queries pass to lift the solution filter. The schema takes the environment in two
 * shapes — `models(environment: ZakekeEnvironment)` and `modelsCount(environment: Int)` — and `null` drops both.
 */
export const ALL_ENVIRONMENTS = { environment: null, environmentID: null } as const;

export const useDashboardEnvironment = (): DashboardEnvironment => {
    const { state } = useAppState();
    return { environment: getQuickAccessEnvironment(state.lastUsedTool) };
};

/**
 * Whether Test Mode can open in Discovery Mode, the sandbox where a tool is switched on to try it. The Configurator
 * has none: Test Mode forces it off for COMPOSER products (`widgets/testPage/testPage.tsx`). The sandbox follows the
 * product it opens on; without one, Test Mode picks a product of the solution in use.
 */
export const useHasDiscoveryMode = (productEnvironment?: ZakekeEnvironment): boolean => {
    const { environment } = useDashboardEnvironment();
    return (productEnvironment ?? environment) !== 'COMPOSER';
};
